import { NUGGET_THEMES, Tier } from '@backrs_ui/common-ui';

import { SchoolLevel } from '../models/models';
import { SEARCH_PAGE_CATEGORIES } from '../pages/Search/search.types';

export const appStrings = {
  modules: {
    home: {
      header: {
        welcome: (name?: string) => `Welcome${name ? `, ${name}` : ''}!`,
        catchUp: 'Catch Up',
        networks: 'Networks',
        searchBarPlaceholder: 'Search',
      },
      cta: {
        title: 'Start by defining your life aspirations',
        description:
          'Get a clear, practical path to your ideal future. First, share what matters to you, what gives you energy, and what you enjoy (and don’t). Then, we’ll match you with career tracks and paths that are fulfilling and financially rewarding.',
        ctaText: 'Build Your Blueprint',
      },
      sections: {
        getBackedYourWay: {
          title: 'Get Backed, Your Way',
          description: `Backrs is a community committed to succeeding in school, great careers, and life. Discover opportunities, get coaching, build competency, and make new connections. Everything is personalized to you and based on FACTS, because we've got your back.`,
          greatCollege: {
            title: 'Go to a Great College',
            description:
              'Tools to help you choose, apply, and finance your post-secondary journey.',
            actionStickerCards: {
              collegeShortlist: {
                title: 'College shortlist',
                description: 'Expand your vision of what colleges might be for you.',
              },
              whatAreYourChances: {
                title: 'What are your chances?',
                description: 'Assess your odds of admission and learn to improve them.',
              },
              essayIdeas: {
                title: 'Essay Ideas',
                description: 'Topics based on your personal experiences and aspirations.',
              },
              improveYourTestScores: {
                title: 'Improve your test scores',
                description: 'Get your ACT and/or SAT scores where you want them.',
              },
            },
          },
          scoreJobInternship: {
            title: 'Score a Job or Internship',
            description:
              'Learn about career opportunities and enhance your ability to apply and land roles.',
            actionStickerCards: {
              updateSocialMedia: {
                title: 'Update Social Media',
                description: 'Optimize your profile to help (not hurt) your odds.',
              },
              jobsYouMightLove: {
                title: 'Jobs you might love',
                description: 'Explore career paths that fit your life aspirations and preferences.',
              },
              interviewing: {
                title: 'Interviewing',
                description: 'Prepare for interviews with confidence and to have fun.',
              },
              boostOfferOdds: {
                title: 'Boost odds of an offer',
                description: 'Assess your fit for a role and how you can improve your odds.',
              },
            },
          },
        },
        exploreOpportunities: {
          title: 'Explore Opportunities',
          description:
            'Scroll through hundreds of opportunities and filter them based on your needs.',
          imageCards: {
            exploreUniversities: {
              title: 'Explore Universities',
              description:
                'Explore potential colleges. Includes FACTs you won’t find anywhere else.',
            },
            discoverJobsInternships: {
              title: 'Discover Jobs & Internships',
              description: 'Explore active positions through our community connections.',
            },
            findScholarships: {
              title: 'Find Scholarships',
              description:
                'Browse vetted scholarships that may be a good fit for your qualifications.',
            },
          },
        },
        upYourGame: {
          title: 'Up Your Game',
          description:
            'Determine the best ways to build networks and a portfolio that will give you the best shot at your life aspirations.',
          buildStrongProfile: {
            title: 'Build a Strong Profile',
            description: 'Strengthen your standing for opportunities that will get you ahead.',
            actionStickerCards: {
              resumeOptimizer: {
                title: 'Resume optimizer',
                description: 'Practical tips to make your resume application-ready.',
              },
              majorsAndCourses: {
                title: 'Majors and Courses',
                description: 'Explore and choose academic classes, majors, and degrees.',
              },
              studying: {
                title: 'Studying',
                description: 'Hacks for studying more efficiently and effectively.',
              },
              boostYourProfile: {
                title: 'Boost your profile',
                description: 'Ideas to help you become the strongest applicant you can be.',
              },
            },
          },
          exploreCareers: {
            title: 'Explore Careers You Might Love',
            description:
              'Learn the difference between career tracks and career paths, and where you stand on both.',
            actionStickerCards: {
              tracksToSucceed: {
                title: 'Tracks to Succeed',
                description: 'Get on and stay on track to achieve your aspirations.',
              },
              shadowing: {
                title: 'Shadowing',
                description: 'Tips to uncover shadowing opportunities.',
              },
              jobsPathsForYou: {
                title: 'Jobs/paths for you',
                description: 'Explore career paths that fit your life aspirations and preferences.',
              },
              successDecoded: {
                title: 'Success Decoded',
                description: 'Understand the markers of success for your chosen career path.',
              },
            },
          },
          buildSocialCapital: {
            title: 'Build Social Capital',
            description:
              'Determine how to form meaningful relationships within your community and build your network.',
            actionStickerCards: {
              networking: {
                title: 'Networking',
                description: 'Learn to build relationships and advance your career',
              },
              commonThreads: {
                title: 'Common threads',
                description: 'Find people in our community & learn your commonalities.',
              },
              seekSupport: {
                title: 'Seeking support',
                description: 'Get comfortable reaching out for help, guidance, and resources.',
              },
              engageTeachers: {
                title: 'Engaging teachers',
                description: 'Build positive relationships with teachers and professors.',
              },
            },
          },
        },
        personalHealthAndWellness: {
          title: 'Personal Health & Wellness',
          description:
            'Mindsets, fundamentals, and guidance to build a healthy and rewarding life. Develop the skills to boost your physical and mental health, from eating well to forming strong interpersonal relationships.',
          attainFinancialFreedom: {
            title: 'Attain Financial Freedom',
            description: 'Tips to finance college, manage money, and build your portfolio.',
            actionStickerCards: {
              extraCash: {
                title: 'Extra Cash',
                description: 'Find ways to earn additional income when needed.',
              },
              lowerTheCostOfCollege: {
                title: 'Lower the cost of college',
                description: 'Find the best sources of financial aid for you',
              },
              buildCredit: {
                title: 'Build Credit',
                description: 'Understand and manage credit and debt responsibly.',
              },
              savingsAndInvesting: {
                title: 'Savings & investing',
                description: 'Learn how to invest money to grow your wealth over time.',
              },
            },
          },
          beWell: {
            title: 'Be Well',
            description: 'Take care of yourself with these emotional and social health tools.',
            actionStickerCards: {
              navigatingStress: {
                title: 'Navigating stress',
                description: 'Understand and cope with feelings of stress and anxiety.',
              },
              sleepRestoration: {
                title: 'Sleep/restoration',
                description: 'Habits to restore energy & improve overall health.',
              },
              familyDynamics: {
                title: 'Family dynamics',
                description: 'Understand and manage relationships within your family.',
              },
              healthcare: {
                title: 'Accessing healthcare',
                description: 'Know how to find and use healthcare services.',
              },
            },
          },
          masterMindsets: {
            title: 'Master Mindsets',
            description:
              'Build the foundations of a great life with these core mindsets and fundamental skills.',
            actionStickerCards: {
              agency: {
                title: 'Agency',
                description: 'Take control of your life and make things happen.',
              },
              learnToLearn: {
                title: 'Learn to learn',
                description: 'Acquire the best habits for study, practice, and experience.',
              },
              growthMindset: {
                title: 'Growth mindset',
                description: 'Learn to use challenges and failure to your advantage. ',
              },
              problemSolve: {
                title: 'Problem solve like a pro',
                description: 'Practice identifying solutions to challenges.',
              },
            },
          },
        },
        completeYourProfile: {
          title: 'Complete your profile',
          description:
            'Backrs works better when we can personalize information to you, based on your preferences and qualifications. Fill out your profile checklist to get started.',
          dataLockedAndLoaded: {
            title: 'Your data, loaded and locked.',
            description:
              'While personalization is at the heart of what we do, your trust and privacy are even more important. That’s why we’ve built an industry-leading privacy policy to ensure your data is always protected. Here’s our promise to you:',
            checkboxItems: [
              'Your data is yours: We will never share your information with anyone without your consent.',
              'No selling out: Your personal data will never be sold to advertisers or companies trying to take advantage of you.',
              'You’re in control: We make it simple to delete all of your data whenever you want.',
            ],
          },
          profileChecklist: {
            title: 'Profile Checklist',
            checklistItems: {
              profilePicture: {
                title: 'Add a profile picture',
                description: 'Upload a professional photo',
              },
              introVideo: {
                title: 'Record an intro video',
                description: 'Introduce yourself',
              },
              uploadDocuments: {
                title: 'Upload your documents',
                description: 'Resume, transcript, test scores',
              },
              profileDetails: {
                title: 'Add profile details',
                description: 'Get the best Backrs experience',
              },
              socialCapitalQuiz: {
                title: 'Who do you know?',
                description: 'Take our social capital quiz',
              },
            },
          },
        },
      },
    },
    college: {
      askLubavPrompts: {
        ask: {
          badgeName: 'Ask',
          title: 'Is this a good fit?',
        },
        pursue: {
          badgeName: 'Pursue',
          title: 'What are my chances?',
        },
      },
      header: {
        buttons: {
          website: 'Website',
          bookmarkNotBookmarked: 'Add to My List',
          bookmarkBookmarked: 'In My List',
        },
      },
      scoop: {
        title: 'The Scoop',
        enrollment: 'Enrollment',
        type: 'Type',
        size: 'Size',
        setting: 'Setting',
        bestKnownFor: 'Best Known For',
        vibeCheck: 'Vibe Check',
        opportunitiesNetworks: 'Opportunities & Networks',
      },
      whoGetsIn: {
        header: {
          title: 'Who gets in?',
          subtitle: 'Admissions Overview',
          admissionsButton: 'Admissions Website',
        },

        earlyDecision: 'Early Decision Deadline',
        applicationDeadline: 'Application Deadline',

        charts: {
          acceptanceRate: {
            title: 'Acceptance Rate',
            tooltip: 'The percentage of applicants who were accepted.',
          },
          highSchoolGPA: {
            title: 'High School GPA',
            tooltip:
              'The 50th percentile GPA score represents the median score of admitted students, meaning half scored above and half scored below. It provides a benchmark for understanding the typical academic performance of students at a particular school.\n\nThe GPA Range represent admitted applicants who fell within the 25th and 75th percentile.',
          },
          satScore: {
            title: 'SAT Score',
            tooltip:
              'The 50th percentile SAT score represents the median score of admitted students, meaning half scored above and half scored below. It provides a benchmark for understanding the typical academic performance of students at a particular school.\n\nThe SAT Range represent admitted applicants who fell within the 25th and 75th percentile.',
            switch: 'Switch to ACT',
          },
          actScore: {
            title: 'ACT Score',
            tooltip:
              'The 50th percentile ACT score represents the median score of admitted students, meaning half scored above and half scored below. It provides a benchmark for understanding the typical academic performance of students at a particular school.\n\nThe ACT Range represent admitted applicants who fell within the 25th and 75th percentile.',
            switch: 'Switch to SAT',
          },
          rigor: {
            title: 'Rigor',
            tooltip: `Students who are admitted typically have this level of rigor or higher when it comes to their coursework\n\nCritically Important: Rigor is essential and non-negotiable for admissions.\n\nVery Important: Rigor plays a major role in admission decisions.\n\nSomewhat Important: Rigor is valued but not the key factor.\n\nLess Important: Rigor has minimal impact on admission decisions.`,
          },
          extracurriculars: {
            title: 'Extracurriculars',
            tooltip: `Students who are admitted typically have this level of extracurriculars or higher\n\nExceptional: Outstanding leadership and high achievement in many activities.\n\nVery Strong: Significant involvement with major leadership and accomplishments.\n\nTypical: Regular participation with some leadership or achievements.\n\nLimited: Minimal extracurricular involvement with little leadership or impact.`,
          },
        },
      },
      theCost: {
        notAvailable: 'N/A',
        header: {
          title: 'The Cost',
          subtitle: 'Cost Overview',
          netPriceButton: 'Calculate your personal net price',
          costByFamilyIncome: 'Cost by Family Income',
          costByFamilyIncomeTooltip:
            'The actual amount you pay for college can vary depending on the availability of federal, state, or institutional grants for your income bracket. The average annual cost represents the net price for students who receive federal financial aid (such as Pell Grants or federal loans) for one academic year. This calculation includes tuition, fees, books, supplies, and typical living expenses at the school, minus the average amount of grants and scholarships awarded. Keep in mind that this is an average based on full-time, first-time students. If the net cost is negative, it means the average grant and scholarship aid exceeded the total cost of attendance.',
        },

        dataTable: {
          familyIncome: 'Family Income',
          averageAnnualCost: 'Average Annual Cost',

          net_price_income_level_0_30000: '$0 - $30,000',
          net_price_income_level_30001_48000: '$30,001 - $48,000',
          net_price_income_level_48001_75000: '$48,001 - $75,000',
          net_price_income_level_75001_110000: '$75,001 - $110,000',
          net_price_income_level_110001_plus: '$110,001 +',
        },

        costFigureTitles: {
          inStateTuition: 'In-State Tuition Cost',
          inStateTuitionTooltip:
            "A school's tuition cost reflects the base price for attending before financial aid, scholarships, or grants are applied. It includes charges for instruction but does not cover additional expenses like housing, meals, and fees.",

          outOfStateTuition: 'Out-of-State Tuition Cost',
          outOfStateTuitionTooltip:
            'Out-of-state tuition is the cost charged to students who do not reside in the same state as the public college or university they are attending. It is typically higher than in-state tuition since state residents benefit from government subsidies that reduce their educational expenses.',

          roomAndBoard: 'Room & Board Fees',
          roomAndBoardTooltip:
            'Room and board refer to the costs of housing and meals while attending college. These expenses vary based on factors such as on-campus or off-campus living, meal plan options, and the location of the school.',

          averageFinancialAid: 'Average Financial Aid Package',
          averageFinancialAidTooltip:
            'This is calculated by looking at the total amount of financial aid a school provides (need based and non-need based grants & scholarships), divided by the total number of students.',
        },
      },
      returnOnInvestment: {
        header: {
          title: 'Return on Investment',
          subtitle: '10 years after graduating',
          askLubavTitle: 'Ask Lubav',
          askLubavDescription: 'Lower your cost of college',
        },
        cards: {
          estimatedInvestment: {
            title: 'Estimated Investment',
            tooltip:
              'This includes your tuition, room & board, as well as school fees. It also includes the  opportunity cost of 166K (~40K a year, for 4 years) of not entering the workforce immediately after graduating high school.',
          },
          estimated10YearReturn: {
            title: 'Estimated 10 Year Earnings',
            tooltip:
              'Earnings are based on the cumulative 10 year median earnings for an individual that has both graduated from the school & received some type of federal aid.',
          },
        },
      },
    },
    inputBox: {
      askLubav: 'Ask Lubav anything!',
    },
    chatBox: {
      copyErr: 'Failed to copy: ',
      commErr: 'Error communicating with AI.',
      overloadErr:
        "We're experiencing exceptionally high demand at the moment. Please check back in a few minutes! In the meantime, check out [Networks](https://my.backrs.com) or [Prosper](https://prosper.backrs.com).",
      fetchErr: 'Error fetching response: ',
      lubavThinking: 'Lubav is thinking',
      fileUpload: {
        submit: 'Submit',
        uploadResume: 'Please upload your resume to continue!',
        uploadTranscript: 'Please upload your transcript to continue!',
        askYourStudentToUploadResume:
          'I can’t seem to find their resume in their profile. Please ask them to upload a recent resume to their profile. Thanks!',
        askYourStudentToUploadTranscript:
          'I can’t seem to find their transcript in their profile. Please ask them to upload a recent transcript to their profile. Thanks!',
        PDForDOCorTXT: 'PDF, DOC or TXT',
        dragAndDrop: 'Drag and drop',
        uploading: 'Uploading...',
        uploadAFile: 'Upload a file',
        iveAttachedResume: 'I’ve just attached my resume to my profile for you.',
        uploadError:
          'I apologize, but there was an error processing your file. Please try again with a different file or format.',
        fileNameDisplay: (fileName: string) => `File: **${fileName}**`,
        fileContentForLLM: (fileName: string, content: string) =>
          `Filename:\n${fileName}\n\nFile content:\n${content}`,
      },
    },
    supercoach: {
      confirmSwitch: 'Are you sure you want to switch users? This will start a new session.',
      supercoachWelcome: 'Start by picking a student above!',
      supercoachSelectPrompt: 'Great! Select a prompt below to get started.',
    },
    header: {
      askLubav: 'Lubav',
      solutionSession: 'Solution Sessions',

      coachingStyleTitle: 'Coaching Style',
      coachingStyleMap: {
        conversational: 'Conversational',
        direct: 'Direct',
        strategic: 'Strategic',
      },

      sourcesOfTruthTitle: 'Sources of Truth',
      sourcesOfTruthMap: {
        all: 'All',
        'backrs-validated': 'Backrs validated',
        'my-networks': 'My networks',
        'my-institution': 'My institution',
        'people-like-me': 'People like me',
      },

      modelTitle: 'Model',
      modelMap: {
        smartest: 'Smartest',
        fastest: 'Fastest',
        thinking: 'Thinking (o1)',
      },

      language: 'Language',
      english: 'English',
      spanish: 'Spanish',
      french: 'French',
      totalCost: 'Total Cost',
      inputTokens: 'Input Tokens',
      outputTokens: 'Output Tokens',
    },
    sidePanel: {
      whoAreWeAdvising: 'Who are we advising today?',
    },
    loginPage: {
      loginFailed: 'Login failed: ',
      loginErr: 'Login failed: Invalid username or password',
      overloadErr:
        "We're experiencing exceptionally high demand at the moment. Please check back in a few minutes! . Please check back in a few minutes! In the meantime, check out Networks or Prosper.",
      noAccess: 'Login failed: You do not yet have access to Lubav AI',
      login: 'Log in to Lubav',
      letsGo: "Let's go!",
      error: {
        invalidEmail: 'Please enter a valid email',
        emailRequired: 'Email is required',
        passwordRequired: 'Password is required',
        login: {
          userNameOrPassword: 'Log in failed: Invalid username or password',
          genericError:
            'An error occurred while logging in. Please try again or contact an administrator for assistance.',
        },
      },
    },
    infoBoxFormatters: {
      youCouldAsk: 'You could ask: ',
    },
    officeHours: {
      categories: {
        learn: 'Learn',
        pursue: 'Pursue',
        connect: 'Connect',
        engage: 'Engage',
      },
      tabs: {
        resources: 'Resources',
        profiles: 'Profiles',
        posts: 'Posts',
        competencies: 'Competencies',
        scholarships: 'Scholarships',
        career: 'Career',
        professionals: 'Professionals',
        students: 'Students',
        providers: 'Providers',
        community: 'Community',
      },
      loadingMessages: [
        'Getting resources...',
        'Looking for relevant contacts...',
        'Finding useful posts...',
        'Fetching interesting videos...',
        'Getting resources...',
      ],
    },
    solutionSessions: {
      selectionBox: {
        coming: 'coming',
        soon: 'soon!',
      },
    },
    login: {
      title: 'Log in to Backrs',
      userNotFound: 'Error getting user information, please try again.',
      skip: "I don't have an account",
    },

    dashboard: {
      prosperPageName: 'prosper',
      header:
        'Welcome! Prosper helps you build the competencies you need to succeed in school, work, and life.',
    },
    search: {
      title: 'Search Backrs',
      searchPlaceholder: 'Search',
      searchPageLabels: {
        [SEARCH_PAGE_CATEGORIES.DEFAULT]: 'Search Backrs',
        [SEARCH_PAGE_CATEGORIES.SCHOLARSHIPS]: 'Search Scholarships',
        [SEARCH_PAGE_CATEGORIES.INTERNSHIPS]: 'Search Internships',
        [SEARCH_PAGE_CATEGORIES.COLLEGES]: 'Search Universities',
      },
      searchPagePlaceholders: {
        [SEARCH_PAGE_CATEGORIES.DEFAULT]: 'Search opportunities, people, and more',
        [SEARCH_PAGE_CATEGORIES.SCHOLARSHIPS]: 'Search schools, majors, and locations',
        [SEARCH_PAGE_CATEGORIES.INTERNSHIPS]: 'Search roles, companies, and locations',
        [SEARCH_PAGE_CATEGORIES.COLLEGES]: 'Search colleges, universities, and locations',
      },
      noResults: 'Your search term did not yield any results.\nTry something else!',
      clear: 'Clear',
      filters: {
        careerOpportunities: 'Career Opportunities',
        scholarships: 'Scholarships',
        resources: 'Resources',
        people: 'People',
      },
      moreFilters: 'More Filters',
    },
    competency: {
      comingSoon: 'Coming Soon',
      pageErrors: {
        returnHome: 'Return Home',
        invalidId: 'Invalid competency ID format.',
        competencyNotFound: 'Competency not found.',
      },
      columnHeaders: {
        learnAndExplore: NUGGET_THEMES.LEARN_AND_EXPLORE,
        growByDoing: NUGGET_THEMES.GROW_BY_DOING,
        realPeopleRealStories: NUGGET_THEMES.REAL_PEOPLE_REAL_STORIES,
      },
      proficiency: {
        widgetTitle: 'My Proficiency',
        widgetButtonLabel: 'Update Status',
        modalTitle: 'How would you rate your proficiency?',
        basic: {
          title: 'Basic',
          description:
            'Has a fundamental understanding of the competency and can perform basic tasks independently. Has discussed the competency with at least one person with at least advanced proficiency. May require guidance or supervision for more complex situations',
        },
        advanced: {
          title: 'Advanced',
          description:
            'Possesses a strong understanding of the competency and can confidently perform a wide range of related tasks. Can apply their knowledge to solve problems and adapt to new situations. Has helped another person achieve basic proficiency.',
        },
        expert: {
          title: 'Expert',
          description:
            'Has a deep and comprehensive understanding of the competency. Can act as a resource for others and solve highly complex problems independently. May be involved in developing new approaches or best practices.',
        },
      },
    },
    bookmarks: {
      mostRecent: 'Most Recent',
      pageHeader: 'Bookmarks',
    },

    markersOfSuccess: {
      pageTitle: 'Markers of Success',
      collegeText: 'Post-Secondary',
      highSchoolText: 'High School',
      button: {
        popoverTitle: 'Compare with...',
        education: 'Education',
        tracks: 'Career Tracks',
      },
    },
    backrsPageShell: {
      menuItems: {
        top: {
          dashboard: 'Dashboard',
          blueprint: 'Blueprint',
          bookmarks: 'Bookmarks',
        },
        middle: {
          academics: 'Academics',
          collegeShortList: 'College Shortlist',
          whatAreYourChances: 'What are your chances?',
          exploreUniversities: 'Explore Universities',
          resumeOptimizer: 'Resume Optimizer',
          majorsAndCourses: 'Majors & Courses',
          improveMyScores: 'Improve Your Test Scores',

          careers: 'Careers',
          tracksForYou: 'Tracks for You',
          pathsForYou: 'Paths for You',
          internshipsJobs: 'Internships/Jobs',
          boostOddsOfAnOffer: 'Boost Odds of an Offer',
          commonThreads: 'Common Threads',
          networking: 'Networking',

          finances: 'Finances',
          extraCash: 'Extra Cash',
          findScholarships: 'Find Scholarships',
          financialAid: 'Financial Aid',
          learnAboutMoney: 'Learn About Money',
          savingsAndInvesting: 'Savings and Investing',

          healthAndSkills: 'Health and Skills',
          growthMindset: 'Growth Mindset',
          navigatingStress: 'Navigating Stress',
          familyDynamics: 'Family Dynamics',
          sleepRestoration: 'Sleep / Restoration',
          seekingSupport: 'Seeking Support',
          problemSolving: 'Problem Solving',
        },
        bottom: {
          supercoach: 'Supercoach',
          superuser: 'Superuser',
          prosper: 'Prosper',
          connect: 'Connect',
          catchUp: 'Catch-up',
          networks: 'Networks',
          panels: 'Panels',
        },
        profile: {
          viewProfile: 'View Profile',
          accountSettings: 'Account Settings',
          myStory: 'My Story',
          signOut: 'Sign Out',
          logIn: 'Log In',
        },
      },
    },
    pageShell: {
      signOut: {
        backrsProfile: 'Backrs Profile',
        logOut: 'Log Out',
        logIn: 'Log In',
      },
      menuItems: {
        prosperDashboard: 'Prosper',
        bookmarks: 'Bookmarks',
        search: 'Search',
        myResults: 'My Blueprint',
        startOver: 'Start Over',
        start: 'Start',
        backrs: 'Backrs',
        prosper: 'Prosper',
        lubav: 'Lubav',
        account: 'Account',
        careerTracks: 'Career Tracks',
        careerPaths: 'Career Paths',
        markersOfSuccess: 'Markers of Success',
      },
    },
    intro: {
      facts: 'FACTS.',
      navigation: 'Click the next fact or use the arrows to navigate',
      slides: {
        slide1: {
          title: '90,000 hours',
          description:
            '–that’s how much you’ll work.  Make it count with a career that gives you purpose, joy, and a solid paycheck.',
        },
        slide2: {
          title: 'You have options.',
          description:
            ' There are dozens of career paths that could make you happy and successful, not just one or two.',
        },
        slide3: {
          title: 'Start strong.',
          description:
            ' Getting on the right track early matters; less than 20% of young adults who start off-track ever move up.',
        },
        slide4: {
          title: 'Create your Blueprint',
          description:
            '– Explore careers, learn what it takes, and set goals to achieve the life you want.',
        },
      },
      header: 'Explore careers, learn what it takes, and set goals to achieve the life you want.',
      buttonTitle: 'Start Here',
    },
    generating: {
      body: {
        one: 'Great news! Your aspirations are within reach.\n\nBefore we share your Blueprint we want you know that...\n\nWe’ve identified ',
        two: ' career paths that could be enjoyable, give you meaning, and pay the bills. That may seem like a lot. But there are many paths that could be good fit for most people. And adults these days change career paths 4-7 times over their lives.\n\nTo achieve your aspirations you’ll need to get on (and stay on) at least the ',
        three:
          ' track.\n\nSo please make sure your review the “markers of success” associated with that track.',
      },
      actionButton: 'See My Blueprint',
      unauthenticatedFooter: { one: 'Have an account? ', two: 'Log in', three: ' here.' },
      error:
        'There was an error with your submission. Please try again or contact support@backrs.com',
    },
    start: {
      getStarted: 'Get Started',
      before: 'Before you begin:\nwhich applies to you?',
      highSchool: 'Blueprint for High School',
      college: 'Blueprint for College',
      startHereButtonText: 'Start Here',
      letsGoButtonText: "Let's Go",
      careerPathDialog: {
        title: "What's a Career Path?",
        content: [
          'A career path is the journey you take within a specific field or profession, encompassing the types of roles you pursue, the tasks you perform, and the ways in which you contribute and find personal fulfillment. It involves not just the job titles you hold but also the skills you develop, the experiences you gain, and the impact you make in your chosen domain. Career paths are often grouped into broad themes like business, science and technology, patient care, education, the arts, and more, each offering a diverse landscape of opportunities.',
          'Within each path, there is a spectrum of roles that range from entry-level positions to highly selective and specialized tracks, reflecting different levels of expertise, responsibility, and compensation. Understanding the breadth and diversity of a career path can help you identify where you currently stand, where you want to go, and what steps you need to take to reach your desired career goals. This awareness enables you to navigate your professional journey more effectively, ensuring that your choices align with your personal values, strengths, and long-term aspirations.',
          'Most professionals will have at least two or three entirely different paths over the course of their lives.',
        ],
      },
      careerTrackDialog: {
        title: "What's a Career Track?",
        bottomContentTop: 'increased income, recognition, opportunity',
        bottomContentBottom: 'higher investment required, competition',
        content: [
          'A career "track" represents your current standing in terms of skill level, achievement, and marketability within the professional landscape. More selective tracks often lead to greater rewards, including higher compensation, increased autonomy, recognition, and opportunities for personal growth and development. However, these tracks typically come with heightened demands, requiring a higher level of education, substantial experience, and the ability to thrive in competitive environments.',
          'It’s crucial to understand that while moving "down" to a less selective track can be relatively straightforward, moving "up" to a more selective track is far more challenging and requires deliberate effort and strategic planning.',
          'By defining a "target track" that aligns with your personal and professional aspirations, you can make more informed decisions regarding your education, work experiences, skill development, and resume building. This clarity not only guides your immediate choices but also helps you create a long-term plan to achieve your career goals.',
        ],
      },
      header: [
        'Define the life you want through a few questions.',
        'If you aren’t sure, share your best guess.',
      ],
    },
    results: {
      minTrackExplanationHeader: 'Your Minimum Track',
      startOver: 'Start Over',
      feedbackMailto:
        'mailto:support@backrs.com?subject=Blueprint Feedback&body=Share any feedback you have about Blueprint. Make sure to export and include your PDF results in the email as well!',
      diagram: {
        'Top-Tier Professional': {
          title: 'TOP',
          tooltip:
            'A top-tier career includes very prestigious jobs like management consultants, investment bankers, top law firm partners, world-class researchers, and specialist doctors. These require the best education, advanced degrees, and significant achievements. The benefits are excellent pay, professional respect, intellectual challenges, and access to powerful networks. The downsides are long hours, frequent travel, high stress, and constant pressure to stay on top, which can affect personal well-being and family life.\n\nAnnual compensation ranges from $90,000 to over $500,000 per year over a career.',
        },
        'Upper-Tier Professional': {
          title: 'UPPER-TIER',
          subtitle: 'professional',
          tooltip:
            'An upper-tier professional career includes selective jobs in business, law, medicine, engineering, and teaching at universities. These roles usually need advanced degrees, lots of experience, and specific skills. The positives include high salaries, chances for promotions, respect in the industry, and decision-making power. The challenges include long hours, high stress, the possibility of burnout, and the need for ongoing education and financial investment.\n\nAnnual compensation ranges from $80,000 to over $350,000 over a career.',
        },
        'Middle-Tier Professional': {
          title: 'MIDDLE-TIER',
          subtitle: 'professional',
          tooltip:
            "Middle-tier jobs include roles like paralegals, project managers, nurses, and teachers. These typically need a bachelor's degree and sometimes extra certifications. The benefits include decent pay, a good balance between work and personal life, and job security. The downsides can be limited opportunities for promotions and the need to keep learning in some fields.\n\nAnnual compensation ranges from $40,000 to $120,000 over a career.",
        },
        Trades: {
          title: 'VOCATIONAL\n/TRADE',
          tooltip:
            'A trades or technical career includes specialized training in fields like plumbing, electrical work, welding, HVAC, and healthcare technician jobs. These usually need certifications instead of a four-year degree. The good side includes moderate pay, job security, and shorter training times. However, the work can be physically tough, sometimes dangerous, and requires ongoing education to keep up with changes in the industry.\n\nAnnual compensation ranges from $40,000 to $110,000 over a career.',
        },
        'Semi-skilled': {
          title: 'SEMI-SKILLED',
          subtitle: '(the default)',
          tooltip:
            'Over 120 million Americans hold lower-skilled or semi-skilled jobs across retail, food service, manual labor, call centers, child care, security, care givers, drivers, receptionists, etc. They earn $25,000-$45,000 per year with limited career advancement opportunities and financial challenges, especially in high-cost living areas.\n\nAround 50% of people in these roles have at least some college education.',
        },
        Entrepreneurship: {
          title: 'ENTREPRENEURSHIP',
          tooltip:
            'Entrepreneurship is about starting and running businesses, owning part of a business, or doing side jobs, focusing on new ideas, taking risks, and making the most of opportunities. The positives are having lots of freedom, the chance to make a lot of money, personal satisfaction, and the ability to influence industries. The negatives include financial risks, uneven income, a heavy workload, and difficulties in balancing work and personal life.\n\nAnnual compensation can range widely from very little to over $500,000 over a career.',
        },
      },
      PDF: {
        saveAsPDF: 'Save as PDF',
        suggestedCareerPaths: 'Potential Career Paths',
      },
      minimumTrackTitle: 'Your Blueprint',
      moreInfo: {
        title: 'Want more info?',
        forSupport: 'For support to achieve your aspirations, join Backrs:',
        information: {
          backrs: 'Networks to set goals and get support to achieve them.',
          prosper: 'Prosper to build the real life competencies.',
          lubav: 'Coach Lubav to solve problems and connect you to people and opportunity.',
        },
      },
      sectionDescriptions: {
        bestFit:
          "These paths are among the best potential fits for you. Select a path to learn more. Don't be surprised if they seem more different than similar!",
        suggested:
          'Each path is rated based on alignment to activities you do (or don’t) enjoy, where you might find meaning, and if you can achieve your financial aspirations. Click a specific path to learn more.',
        markersOfSuccess: (track: Tier, schoolLevel: SchoolLevel) =>
          `Below you'll find key activities and milestones for a student in ${schoolLevel === 'high_school' ? 'high school' : 'college'} that will ensure you get on and stay on at least the ${track} track.`,
      },
      bestFitCareerPath: 'Best Fit Career Paths',
      suggestedCareerPaths: 'Potential Career Paths',
      suggestedFits: {
        strong: 'Strong Fit',
        medium: 'Medium Fit',
        low: 'Low Fit',
      },
      aspirationsTitle: 'Your Aspirations',
      aspirationsExplanation:
        'Your responses indicated the following aspirations. To get different results, feel free to take the Blueprint quiz again!',
      'Semi-skilled': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a semi-skilled track or higher.',
        description:
          'Lower-skilled and semi-skilled career tracks include jobs that require little to no formal education or specialized training. Lower-skilled jobs typically involve manual labor or routine tasks, such as working as a cashier, fast food worker, or janitor, with median annual incomes ranging from $25,000 to $30,000. Semi-skilled jobs require some training or experience but not a college degree, with roles like administrative assistants, truck drivers, and customer service representatives earning median incomes of $35,000 to $45,000. These roles are essential to the economy but don’t provide the same opportunities as more skilled careers.\n\nThere are some advantages to these career paths, like starting work right after high school and having job opportunities in various locations. However, the downsides are significant. Lower-skilled and semi-skilled jobs usually pay low wages, offer limited career growth, and can be physically demanding. People in these jobs often struggle to afford basic necessities, like housing and healthcare. These roles also lack job security and benefits, making it difficult to plan for the future or build long-term financial stability.\n\nMany people who end up in these jobs start without a college degree, but surprisingly, a large number have some post-secondary education. Nearly half of workers in these roles have attended college or earned a degree, but for various reasons they end up in lower-paying jobs. While these jobs are common, they are not attractive long-term paths for those seeking financial stability or career growth, and young people should aim for careers that offer more opportunities for success and security.',
        entrepreneurshipText: '',
      },
      Trades: {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a trades or technical track or higher.',
        description:
          "A trades or technical career path involves getting special training in areas like plumbing, electrical work, welding, HVAC systems, and healthcare jobs like medical assistants, radiologic technologists, and dental hygienists. Some good things about this career path include the chance to earn a good salary, job stability because there is always a need for skilled workers, and the fact that you don’t need to spend as much time in school as you would for a four-year college degree. Starting pay is usually between $40,000 and $55,000, and with experience, it can go up to $55,000 to $85,000, and sometimes as high as $110,000. While this pay offers some chance to build wealth, it's not huge. However, if you combine this career with owning your own business, you could make a lot more money and build more wealth.\n\nSome downsides include the work being physically tough, possibly dangerous, and needing to keep learning new skills as things change in your industry. But even with these challenges, many people find this kind of hands-on work very satisfying because it lets them develop real skills.",
        entrepreneurshipText:
          '\n\nEntrepreneurship can significantly enhance a trades or technical career path by offering skilled workers opportunities beyond traditional employment. For example, a plumber, electrician, or HVAC technician can start their own business, allowing them to set their own rates, choose their projects, and potentially employ others, leading to higher earning potential and more control over their work-life balance. In addition to running their own businesses, tradespeople can engage in side hustles, offering services during off-hours or weekends to supplement their income without leaving full-time jobs, providing flexibility with lower risk. Furthermore, trades professionals can build equity in existing firms by becoming part-owners or partners, enabling them to share in the company’s growth and profits without managing every aspect of the business. Whether through business ownership, side hustles, or equity-building, entrepreneurship allows trades professionals to expand their financial potential while maintaining the hands-on, practical work they find fulfilling.',
      },
      'Middle-Tier Professional': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a mid-tier professional track or higher.',
        description:
          'Middle-tier professionals include jobs like paralegals, some lawyers, project managers, marketing specialists, human resources managers, nurses, teachers, many sales roles, and IT support specialists. These jobs usually require a bachelor’s degree and sometimes extra certifications. Some benefits of this path are the chance to earn a decent salary, some chances for promotions, and jobs that often provide a good balance between work and personal life. Starting pay is usually between $40,000 and $50,000, and with more experience, it can rise to $55,000 to $100,000, and sometimes up to $120,000. While this salary can help you build some wealth, it’s not a huge amount. However, if you combine this career with starting your own business, you could earn a lot more and build more wealth.\n\nSome downsides include the need to keep learning and getting new certifications, the possibility of too many people competing for the same jobs in some fields, and sometimes being stuck at certain job levels unless you get more education. Despite these challenges, many people enjoy the steady career path, the variety of job choices, and the chance to make a difference in roles like nursing, teaching, and IT support.',
        entrepreneurshipText:
          '\n\nMiddle-tier professionals can incorporate entrepreneurship in several ways. They might start a consulting business in areas like project management, marketing, or HR, using their expertise to serve clients, potentially while keeping their full-time job. They could also launch a side business, such as freelance IT support or tutoring, to boost income. Another option is building equity in an existing firm by becoming a partner or investor, benefiting from its growth while applying their skills. Professionals like nurses or teachers might also create and sell online courses or resources, increasing their earnings and career flexibility.',
      },
      'Upper-Tier Professional': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on an upper-tier professional track or higher.',
        description:
          'An upper-tier professionals track includes selective careers in business, accounting, consulting, selective technology and engineering roles, corporate law, medicine, and top positions in education. These jobs often require advanced degrees, experience, and high skill levels. Starting pay usually ranges from $80,000 to $200,000, with experienced professionals earning $150,000 to $350,000 or more, sometimes exceeding $500,000. This pay allows for a wealthy lifestyle and significant savings.\n\nPeople in these roles hold decision-making power and influence, earning high respect in their industries. They often advance to leadership positions and receive major recognition.\n\nHowever, these jobs come with challenges like heavy responsibility and stress. Upper-tier professionals often work long, unpredictable hours, making work-life balance difficult. They must keep learning to stay current in their fields, which requires time and money. The combination of stress, long hours, and responsibility can lead to burnout if not managed well. Despite these challenges, many find the rewards make it worthwhile.',
        entrepreneurshipText:
          '\n\nUpper-tier professionals can incorporate entrepreneurship into their careers in several impactful ways. For instance, corporate lawyers or physicians can establish their own firms or private practices, giving them more control over their client base and fees while leveraging their expertise. Professionals in accounting, consulting, or engineering might start boutique firms or niche consulting businesses, providing specialized services and building their reputation independently of large corporations. Additionally, they can become investors or equity partners in startups or existing firms, using their industry knowledge to guide business strategy while generating passive income. Some might also create thought leadership platforms, such as writing books, developing online courses, or hosting webinars, to monetize their expertise while enhancing their professional brand. These entrepreneurial ventures offer increased financial freedom, greater flexibility, and the opportunity to shape their careers on their terms.',
      },
      'Top-Tier Professional': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a top-tier professional track or higher.',
        description:
          'A top-tier career track includes highly competitive jobs like management consulting, investment banking, top law firms, world-class research, and specialist medicine. These roles require the best educational qualifications, advanced degrees, and major professional achievements.\n\nPeople in these careers earn significant pay. Starting salaries range from $90,000 to $200,000, and with experience, can reach $250,000 to $500,000, or even over $1,000,000. This level of income allows for a wealthy lifestyle and substantial savings.\n\nThese jobs come with respect and recognition in their fields and society. The work is often interesting and impactful, offering opportunities to collaborate with influential people and build valuable connections.\n\nHowever, they are demanding. Long hours, frequent travel, and high-pressure environments make work-life balance difficult. Competition is fierce, requiring constant high performance, which can be mentally and emotionally draining. Continuous skill development is necessary, consuming time and energy. The stress, long hours, and performance demands can lead to burnout, but many find working at the top level and making a difference worthwhile.',
        entrepreneurshipText:
          '\n\nTop-tier professionals can incorporate entrepreneurship by starting boutique consulting firms, private practices, or surgery centers, giving them more control over clients and earnings. They can also invest in or advise startups, using their expertise and networks to build equity and foster innovation. Additionally, they may create thought leadership platforms through books, keynote speeches, or exclusive training programs, enhancing their reputation and generating extra income. These entrepreneurial ventures offer greater financial rewards, increased autonomy, and the chance to expand their influence while maintaining their primary high-demand careers.',
      },
      Entrepreneurship: {
        aspirationsIndicate:
          'Your aspirations indicate that you would have to be at minimum an entrepreneur to achieve your ideal lifestyle.',
        description:
          'An entrepreneurship track encompasses a wide spectrum of business ownership and ventures, including starting and running a business, becoming a partial owner in a professional practice, or engaging in side hustles alongside a primary career. This path is defined by its emphasis on innovation, risk-taking, and the pursuit of new market opportunities. Entrepreneurs often create their own opportunities rather than following a traditional career path, leveraging their skills, passion, and creativity to generate income.\n\nFinancial outcomes in entrepreneurship can vary widely. Successful entrepreneurs can achieve significant wealth, potentially earning incomes that surpass those in traditional upper-tier professional roles. However, earnings can also be inconsistent, particularly in the early stages of a venture. Many entrepreneurs experience periods of financial uncertainty and must be willing to reinvest in their businesses to achieve long-term success. While some may reach multi-million-dollar revenues, others may supplement their income with smaller-scale side hustles that provide flexibility and additional revenue streams.\n\nEntrepreneurship offers considerable autonomy, allowing individuals to make key decisions and directly shape the direction of their ventures. This control can lead to a high level of personal satisfaction and the fulfillment of creating something unique. Entrepreneurs also have the opportunity to influence their industry, drive innovation, and potentially make a positive impact on society through their products, services, or business practices.\n\nHowever, this path comes with significant challenges. Entrepreneurs often face considerable risks, including financial losses, market competition, and the pressure of managing all aspects of a business. The workload can be intense, especially during the startup phase, with long hours and a lack of guaranteed income. Success requires adaptability, resilience, and a continuous commitment to learning and growth. Balancing work and personal life can be difficult, as the demands of running a business can be all-consuming.\n\nDespite these challenges, many entrepreneurs find the potential rewards—financial independence, personal growth, and the satisfaction of building a successful venture—to be well worth the risks and efforts involved.',
        entrepreneurshipText: '',
      },
      markersOfSuccessSectionTitle: 'Markers of Success',
    },
    survey: {
      facts: 'FACTS.',
      highSchoolCollegeQuestion: {
        title: 'Current Enrollment',
        question: 'What type of educational institution are you currently attending, if any?',
        options: {
          high_school: 'High School',
          college: 'College/University',
          trade: 'Trade/Vocational School',
          no_current_enrollment: 'No current enrollment',
        },
      },
    },
  },
  common: {
    coachLubav: 'Coach Lubav',
    button: {
      next: 'Next',
      back: 'Back',
      letsGo: "Let's Go!",
      viewMore: 'View More +',
      viewLess: 'View Less -',
      previous: 'Previous',
    },
  },
  tours: {
    restartTourLater: 'Click here anytime to take this tour again.',
    introPage: {
      step1:
        'We’re going to ask a few questions about the life you want, what you do (and don’t) enjoy doing, how you find meaning, and more. You probably won’t know some of the answers for sure, but share your best guess!',
      step2:
        'Based on the answers you gave on the quiz, we’ll suggest a minimum career track you should aim for. Click here to learn more about what “minimum career track” means.',
      step3:
        'Career tracks are academic and career trajectories that involve different levels of investment, reward, and leadership opportunity. Learn more about the 6 career tracks here.',
      step4: 'Click here to return to this screen whenever you need a refresher on Career Tracks.',
      step5:
        'After suggesting a minimum career track, we’ll share some career paths we think you’ll enjoy exploring. You can click here to learn how we define a “career path.”',
      step6:
        'Career paths are professional trajectories within specific fields. They each encompass many different roles that have related skill requirements, functions, or responsibilities. Learn more here, then click on any of these 70 career paths to get to know them!',
      step7: 'Click here to return to this screen whenever you want to explore more career paths.',
      step8:
        'Use your Blueprint results to create some school, work, or life goals for yourself. These should help you stay on track to build a career that will support your lifestyle aspirations. Don’t forget to record your goals on the Backrs app!',
      step9:
        'You can repeat this quiz as many times as you like. Ready to get started? Click here!',
    },
    results: {
      step1:
        'Here’s a summary of your responses to the Blueprint quiz. These aspirations will form the basis of the recommendations on your blueprint.',
      step2:
        'Are you on track to get what you want out of life? We recommend pursuing educational or professional opportunities that require this level of investment and offer this level of reward in order to achieve your lifestyle aspirations.',
      step3:
        'The decisions you make as a young adult impact which of the following 6 career tracks you end up on as you get older. Once you’re established on a particular track, it’s difficult to move up to one that’s more selective and requires more investment.',
      step4:
        'Here, you’ll find the career paths we think would be strong, medium, or low fits for you based on the aspirations you shared during your quiz. Click one to learn more!',
      step5:
        'Here, you can review some key stats regarding job availability, role distribution across career tracks, and industry trends in this career path.',
      step6:
        'Get personalized recommendations from Lubav on roles within this career path that would suit your aspirations.',
      step7:
        'Here are the steps we recommend you take to ensure you get on the minimum career track you need to achieve your lifestyle aspirations.',
      step8: 'Click on the “plus” button for details on each marker of success we recommend.',
      step9:
        'Make sure to save and/or share your Blueprint. You can send it to your backrs, guidance counselor, or another person supporting you on your academic and career journey.',
    },
  },
} as const;
